import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '@/config';
import { gravatarUrl } from '@/utils/utils';

export interface UserProfile {
  email: string;
  name: string;
  picture: string;
  exp: number;
  sub: string;
  streams: any[];
  workspaces: any[];
}

interface AuthContextType {
  userProfile: UserProfile | null;
  setUserProfile: (profile: UserProfile | null) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  handleLogout: () => void;
  initiateGoogleLogin: () => void;
  apiRequest: (url: string, options?: RequestInit) => Promise<Response>;
  initializeAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  // Function to fetch Google profile picture if needed
  const fetchGoogleProfilePicture = async (token: string, email: string): Promise<string | null> => {
    try {
      // Try to fetch user info from Google People API
      const response = await fetch('https://people.googleapis.com/v1/people/me?personFields=photos', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Google People API response:', data);
        
        // Extract profile picture URL
        if (data.photos && data.photos.length > 0) {
          const photoUrl = data.photos[0].url;
          console.log('Found Google profile picture:', photoUrl);
          return photoUrl;
        }
      } else {
        console.log('Failed to fetch Google profile picture, status:', response.status);
      }
      
      // Fallback to Gravatar
      const gravatarFallback = gravatarUrl(email, 200);
      console.log('Using Gravatar fallback:', gravatarFallback);
      return gravatarFallback;
    } catch (error) {
      console.error('Error fetching Google profile picture:', error);
      return gravatarUrl(email, 200);
    }
  };

  const initializeAuth = useCallback(async () => {
    try {
      console.log('Initializing auth...');
      
      // Get token from URL or localStorage
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      const token = tokenFromUrl || localStorage.getItem('auth_token');
      
      if (token) {
        console.log('Using token from URL/localStorage for auth initialization');
        localStorage.setItem('auth_token', token);
        // Also set as cookie for backup
        document.cookie = `auth_token=${token}; path=/; max-age=3600; samesite=lax`;
      }
      
      const response = await fetch(`${config.API_BASE_URL}/api/v1/user/me`, {
        credentials: 'include',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Received user data:', data);
        
        // Process the profile picture URL
        let profilePicture = '';
        
        // Check if we have a picture from Google
        if (data.picture) {
          // Ensure the picture URL uses HTTPS
          profilePicture = data.picture.replace(/^http:\/\//i, 'https://');
          
          // If the URL doesn't have a size parameter and it's a Google profile picture, add one
          if (profilePicture.includes('googleusercontent.com') && !profilePicture.includes('=s')) {
            // Add a size parameter for better quality (128px)
            profilePicture = `${profilePicture}=s128-c`;
          }
          
          console.log('Using profile picture URL:', profilePicture);
        } else if (token && data.email) {
          // Try to fetch the profile picture directly from Google
          console.log('No picture URL provided, attempting to fetch from Google...');
          const googlePicture = await fetchGoogleProfilePicture(token, data.email);
          if (googlePicture) {
            profilePicture = googlePicture;
          }
        } else {
          console.log('No profile picture URL provided by the API');
        }
        
        // Store the complete user profile in localStorage
        const userProfile: UserProfile = {
          email: data.email,
          name: data.name || data.email.split('@')[0] || 'User',
          picture: profilePicture,
          exp: Math.floor(Date.now() / 1000) + 3600,
          sub: data.id,
          streams: data.streams || [],  // Store complete stream objects
          workspaces: data.workspaces || []  // Store complete workspace objects
        };
        
        console.log('Setting user profile:', userProfile);
        localStorage.setItem('user_profile', JSON.stringify(userProfile));
        setUserProfile(userProfile);
        setIsAuthenticated(true);
        
        // Handle post-login redirect
        const preLoginPath = sessionStorage.getItem('preLoginPath');
        if (preLoginPath) {
          sessionStorage.removeItem('preLoginPath');
          navigate(preLoginPath);
        }
      } else {
        console.log('Auth initialization failed - response not ok');
        console.log('Response status:', response.status);
        try {
          const errorData = await response.json();
          console.log('Error details:', errorData);
        } catch (e) {
          console.log('No JSON error details available');
        }
        
        setIsAuthenticated(false);
        setUserProfile(null);
        localStorage.removeItem('user_profile');
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      setIsAuthenticated(false);
      setUserProfile(null);
      localStorage.removeItem('user_profile');
      localStorage.removeItem('auth_token');
    }
  }, [navigate]);

  const initiateGoogleLogin = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/v1/user/auth/google/authorize`, {
        credentials: 'include'  // Important for cookie handling
      });
      const data = await response.json();
      
      if (data.authorization_url) {
        // Store current location for post-login redirect
        sessionStorage.setItem('preLoginPath', window.location.pathname);
        window.location.href = data.authorization_url;
      } else {
        throw new Error('Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Failed to initiate Google login:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${config.API_BASE_URL}/api/v1/user/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      
      // Clear all auth state
      setUserProfile(null);
      setIsAuthenticated(false);
      
      // Remove stored data
      localStorage.removeItem('user_profile');
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('preLoginPath');
      
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Define a type for the apiRequest function with the cachedToken property
  type ApiRequestFunction = {
    (url: string, options?: RequestInit): Promise<Response>;
    cachedToken: string | null;
  };

  // Updated API request wrapper with better error handling and token caching
  const apiRequest = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    try {
      // Use a static variable to cache the token
      if (!(apiRequest as ApiRequestFunction).cachedToken) {
        // Get token from multiple possible sources
        (apiRequest as ApiRequestFunction).cachedToken = 
          localStorage.getItem('auth_token') || 
          document.cookie.split(';').find(c => c.trim().startsWith('auth_token='))?.split('=')[1] ||
          document.cookie.split(';').find(c => c.trim().startsWith('Authorization='))?.split('=')[1] ||
          document.cookie.split(';').find(c => c.trim().startsWith('session='))?.split('=')[1] || 
          null;
        
        console.log('Caching token for API requests:', 
          (apiRequest as ApiRequestFunction).cachedToken 
            ? `${(apiRequest as ApiRequestFunction).cachedToken!.substring(0, 10)}...` 
            : 'No token found'
        );
      }
      
      const response = await fetch(url, {
        ...options,
        credentials: 'include',  // Important for sending cookies
        headers: {
          ...options.headers,
          'Content-Type': 'application/json',
          ...((apiRequest as ApiRequestFunction).cachedToken 
            ? { 'Authorization': `Bearer ${(apiRequest as ApiRequestFunction).cachedToken}` } 
            : {})
        }
      });

      if (response.status === 401) {
        console.log('Authentication failed - redirecting to login');
        // Clear the cached token on auth failure
        (apiRequest as ApiRequestFunction).cachedToken = null;
        setIsAuthenticated(false);
        setUserProfile(null);
        localStorage.removeItem('auth_token');
        navigate('/login');
        throw new Error('Authentication required');
      }

      return response;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }, [navigate]);
  
  // Initialize the cachedToken property
  (apiRequest as ApiRequestFunction).cachedToken = null;

  // Effect to handle OAuth callback and initialization
  useEffect(() => {
    // Only run once
    const callbackHandled = sessionStorage.getItem('oauth_callback_handled');
    if (callbackHandled) return;
    
    const handleCallback = async () => {
      // Check for token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      const error = urlParams.get('error');
      
      if (error) {
        console.error('OAuth error:', error);
        navigate('/login');
        return;
      }

      // If token is in URL, save it and use it
      if (tokenFromUrl) {
        console.log('Found token in URL, saving to localStorage and cookies');
        localStorage.setItem('auth_token', tokenFromUrl);
        
        // Also manually set the token in a cookie that JS can access
        document.cookie = `auth_token=${tokenFromUrl}; path=/; max-age=3600; samesite=lax`;
        
        // Clean up URL by removing token parameter
        const newUrl = window.location.pathname + 
          window.location.search.replace(/[?&]token=[^&]+/, '') + 
          window.location.hash;
        window.history.replaceState({}, document.title, newUrl);
        
        // Initialize auth with the new token
        await initializeAuth();
        sessionStorage.setItem('oauth_callback_handled', 'true');
      }
      // If we're on the callback route or dashboard (after redirect)
      else if (window.location.pathname.includes('/auth/google/callback') || 
          window.location.pathname.includes('/dashboard')) {
        console.log('Initializing auth after OAuth callback/redirect');
        await initializeAuth();
        
        // Only clean up URL if we're still on the callback route
        if (window.location.pathname.includes('/auth/google/callback')) {
          navigate('/dashboard', { replace: true });
        }
        
        sessionStorage.setItem('oauth_callback_handled', 'true');
      }
    };

    handleCallback();
  }, [navigate, initializeAuth]);

  // Add effect to restore profile from localStorage on mount
  useEffect(() => {
    const storedProfile = localStorage.getItem('user_profile');
    if (storedProfile) {
      try {
        const profile = JSON.parse(storedProfile);
        console.log('Restoring profile from localStorage:', profile);
        setUserProfile(profile);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Failed to parse stored profile:', error);
        localStorage.removeItem('user_profile');
      }
    }
  }, []);

  return (
    <AuthContext.Provider 
      value={{ 
        userProfile, 
        setUserProfile, 
        isAuthenticated, 
        setIsAuthenticated,
        initializeAuth,
        handleLogout,
        initiateGoogleLogin,
        apiRequest,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}